import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { userApi } from '@/api';
import { QUERY_KEY_CONST } from '@/constant';
import usePaginationSearchParams from '@/hook/tanstackQuery/usePaginationSearchParams';
import type { User, UserApi, UserRole } from '@/type';

export const useUserListQuery = () => {
  const [searchParams] = useSearchParams();

  const paginationSearchParams = usePaginationSearchParams('user');
  const role = searchParams.get('userRole');

  const listParams = {
    ...paginationSearchParams,
    role: role as UserRole,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  return useQuery({
    queryFn: () =>
      userApi.getList({ ...listParams }).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.USER_KEYS.getList(listParams),
  });
};

export const useUserCountQuery = ({ role }: { role?: UserRole }) => {
  return useQuery({
    queryFn: () =>
      userApi
        .getCount({
          role,
        })
        .then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.USER_KEYS.getCount({ role }),
  });
};

export const useUserUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: UserApi.Update.Request) => {
      return userApi.update(param);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
    },
  });
};

export const useUserRemoveQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userID: User['id']) => {
      return userApi.remove({ id: userID });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
    },
  });
};
